<!-- Hero Section -->
<section class="relative py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
  <div class="absolute inset-0 opacity-20">
    <img src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=1920&h=800&fit=crop" alt="" class="w-full h-full object-cover">
  </div>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <div class="text-center text-white">
      <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Enterprise Messaging Services</h1>
      <p class="text-xl text-white/90 max-w-3xl mx-auto mb-8">
        Connect with your audience through SMS, Email, WhatsApp, and RCS. Powerful multi-channel messaging solutions that deliver results.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="#services" class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all shadow-lg">
          Explore Services
        </a>
        <a routerLink="/contact" class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all">
          Get Started
        </a>
      </div>
    </div>
  </div>
</section>

<!-- Stats Section -->
<section class="py-16 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
      @for (stat of stats; track stat.label) {
        <div class="text-center">
          <div class="text-4xl md:text-5xl font-bold text-primary-600 mb-2">{{ stat.value }}</div>
          <div class="text-gray-600 font-medium">{{ stat.label }}</div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Services Grid Section -->
<section id="services" class="py-20 bg-gray-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Messaging Services</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Choose the right channel for your message. From traditional SMS to modern RCS, we provide comprehensive solutions for all your communication needs.
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (service of services; track service.title) {
        <a [routerLink]="service.link" class="group card overflow-hidden hover:-translate-y-2 transition-all duration-300 cursor-pointer">
          <div class="h-48 overflow-hidden">
            <img [src]="service.image" [alt]="service.title" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500">
          </div>
          <div class="p-6">
            <h3 class="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">{{ service.title }}</h3>
            <p class="text-gray-600 mb-4">{{ service.description }}</p>
            <span class="text-primary-600 font-semibold flex items-center">
              Learn More
              <svg class="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </span>
          </div>
        </a>
      }
    </div>
  </div>
</section>

<!-- Case Studies Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        See how leading organizations leverage our messaging platform to achieve their business goals
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (study of caseStudies; track study.title) {
        <div class="card overflow-hidden hover:shadow-xl transition-all">
          <div class="h-48 overflow-hidden">
            <img [src]="study.image" [alt]="study.title" class="w-full h-full object-cover">
          </div>
          <div class="p-6">
            <div class="text-primary-600 font-semibold mb-2">{{ study.client }}</div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">{{ study.title }}</h3>
            <p class="text-gray-600 mb-4">{{ study.description }}</p>
            <div class="space-y-2">
              <div class="text-sm font-semibold text-gray-900 mb-2">Key Results:</div>
              @for (result of study.results; track result) {
                <div class="flex items-start text-sm text-gray-700">
                  <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                  </svg>
                  <span>{{ result }}</span>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Testimonials Section -->
<section class="py-20 bg-gradient-to-br from-primary-50 to-primary-100">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
      <p class="text-xl text-gray-600">Trusted by thousands of businesses worldwide</p>
    </div>

    <div class="grid md:grid-cols-3 gap-8">
      @for (testimonial of testimonials; track testimonial.name) {
        <div class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
          <div class="flex items-center mb-6">
            <img [src]="testimonial.avatar" [alt]="testimonial.name" class="w-16 h-16 rounded-full object-cover mr-4">
            <div>
              <div class="font-bold text-gray-900">{{ testimonial.name }}</div>
              <div class="text-sm text-gray-600">{{ testimonial.role }}</div>
              <div class="text-sm text-primary-600 font-semibold">{{ testimonial.company }}</div>
            </div>
          </div>
          <div class="flex mb-4">
            @for (star of [1,2,3,4,5]; track star) {
              <svg class="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
              </svg>
            }
          </div>
          <p class="text-gray-700 italic leading-relaxed">{{ testimonial.content }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
    <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Communication Strategy?</h2>
    <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
      Join thousands of businesses that trust HoneyKode for their messaging needs. Get started today with our enterprise-grade platform.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center">
      <a routerLink="/contact" class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all shadow-lg">
        Schedule a Demo
      </a>
      <a routerLink="/contact" class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all">
        Contact Sales
      </a>
    </div>
  </div>
</section>
